/**
 * @yanolja-org/core-ui — widgets/CoreUIDataProvider/types.ts
 *
 * Provider 가 사용하는 공유 타입. domain key, env, entry state machine,
 * fetch error 분류 등 외부에 노출되는 타입은 `src/types.ts` 에서 re-export.
 */

import type { CategoryOverviewResponse } from '../CategoryWidget/api-types';
import type { CartResponse } from '../HeaderCartActionWidget/api-types';
import type { BiResponse } from '../HeaderLogoWidget/api-types';

/**
 * 도메인별 응답 shape 의 single source of truth.
 *
 * host 가 `fetchers` prop 으로 fetcher 주입 시 *반환 type 이 도메인 별 shape 으로 강제* 되어,
 * BFF/API gateway 의 raw 응답을 widget 이 기대하는 shape 으로 변환하는 어댑터를 강제할 수 있음.
 *
 * 예: 호스트의 navigation-bar 응답 `{ cart: { count: number } }` 을 cart 도메인의
 * `{ hasItem: boolean }` 으로 변환하는 어댑터:
 * ```tsx
 * fetchers={{
 *   cart: async (signal) => {
 *     const nav = await fetchNavigationBar({ signal });
 *     return { hasItem: (nav?.cart?.count ?? 0) > 0 };
 *   },
 * }}
 * ```
 *
 * 도메인 추가 시 이 인터페이스 한 곳만 확장 — `DomainKey` 가 `keyof DomainResponseMap` 으로
 * derive 되므로 union 도 자동 따라옴.
 *
 * @see plan/cart-and-fetcher-override-design.md — §5-1
 */
export interface DomainResponseMap {
  category: CategoryOverviewResponse;
  bi: BiResponse;
  cart: CartResponse;
}

/**
 * 데이터 도메인 식별자. `DomainResponseMap` 의 key 로부터 derive.
 *
 * `'cart'` 는 host fetcher override 가 필수인 user-specific 도메인 — url-table 미등록.
 * `'category'` / `'bi'` 는 url-table 의 env-based fetcher 가 default.
 *
 * @see plan/category-widget-design.md — Q13
 * @see plan/cart-and-fetcher-override-design.md — §5-2
 */
export type DomainKey = keyof DomainResponseMap;

/**
 * 호스트가 Provider 에 전달하는 환경 식별자.
 *
 * core-ui 가 env → URL/headers 매핑을 내장 (env 만 받음 정책 — Q4-A).
 * 호스트는 `process.env.NEXT_PUBLIC_ENV` 등 자체 source 에서 결정.
 */
export type Env = 'local' | 'dev' | 'qa' | 'stage' | 'prod';

export const VALID_ENVS = ['local', 'dev', 'qa', 'stage', 'prod'] as const;

/**
 * BFF 가 응답 분기에 사용하는 platform 식별자. `Nol-Platform` HTTP 헤더로 전송.
 *
 * - `'Web'` — desktop 브라우저
 * - `'MWeb'` — mobile 브라우저
 *
 * 호스트가 device detect 결과를 기반으로 결정 (cookie/UA header SSR 결정 권장).
 * url-table 기반 fetch (category / bi) 시 Provider 가 `'Nol-Platform': platform` 을 자동 합성.
 * host fetcher override (`fetchers.*`) 는 platform 못 받음 — 호스트가 자체 environment 에서 직접 처리.
 */
export type Platform = 'Android' | 'iOS' | 'Web' | 'MWeb';

export const VALID_PLATFORMS = ['Android', 'iOS', 'Web', 'MWeb'] as const;

/**
 * fetch 실패 시 정규화된 에러 형태.
 *
 * - `network`: fetch 자체가 throw (offline, DNS 실패 등)
 * - `http`: response 가 도착했지만 `res.ok === false`
 * - `parse`: `res.json()` 이 throw (malformed JSON)
 *
 * 'abort' 는 별도 enum 으로 두지 않음 — abort 는 silent (Q16 정책).
 *
 * @see plan/category-widget-design.md — Q14
 */
export type FetchError =
  | { kind: 'network'; cause: Error }
  | { kind: 'http'; status: number }
  | { kind: 'parse'; cause: Error };

/**
 * 도메인 entry 의 상태 머신.
 *
 * 전이:
 * - `idle` → `loading` (ensureFetched 호출 시)
 * - `loading` → `success` (fetch 완료)
 * - `loading` → `error` (fetch 실패)
 * - `success` | `error` → `idle` (retry 호출 시) → `loading`
 *
 * dedup: 이미 `loading` / `success` / `error` 상태인 도메인은 ensureFetched 가 no-op.
 *
 * @see plan/category-widget-design.md — Q6, Q16
 */
export type EntryState<TData = unknown> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: TData }
  | { status: 'error'; error: FetchError };

/**
 * Host 가 도메인별로 주입하는 fetcher 함수 시그니처.
 *
 * - `AbortSignal` 받음 — Provider 가 unmount / refetch 시 `controller.abort()` 호출.
 *   호스트가 이 signal 을 underlying fetch 에 전달하면 *진짜 네트워크 취소*. 안 전달해도 *정확성 문제 없음*
 *   (Provider 의 then 콜백이 stale controller 의 결과를 검사로 drop). 자원 효율 차이만 있음. 자세한 trade-off 는 §10 참조
 * - `Promise<DomainResponseMap[K]>` 반환 — TS 가 도메인별 expected shape 강제 (어댑터 강제)
 * - reference 안정성 불필요 — Provider 가 ref 로 latest 보관 (useCallback 강제 X)
 *
 * generic 의 default `K = DomainKey` 는 untyped 호출 시 union return 만 fallback — 실제 호출에선
 * `fetchers` 의 key 로부터 K 가 도메인별로 좁혀지므로 호스트는 단순히 매 fetcher 의 return type 만
 * 일치시키면 됨.
 *
 * 사용 (권장 — signal 전달):
 * ```ts
 * fetchers={{
 *   cart: async (signal) => {
 *     const nav = await fetchNavigationBar({ signal });
 *     // TS 가 { hasItem: boolean } 강제 (CartResponse 와 일치해야 함)
 *     return { hasItem: (nav?.cart?.count ?? 0) > 0 };
 *   },
 * }}
 * ```
 *
 * 사용 (signal 무시 — 정확성 OK, 대역폭만 일부 낭비):
 * ```ts
 * fetchers={{
 *   cart: async () => {
 *     const nav = await fetchNavigationBar();
 *     return { hasItem: (nav?.cart?.count ?? 0) > 0 };
 *   },
 * }}
 * ```
 *
 * @see plan/cart-and-fetcher-override-design.md — §5-1
 */
export type HostFetcher<K extends DomainKey = DomainKey> = (signal: AbortSignal) => Promise<DomainResponseMap[K]>;

/**
 * fetchEntry 호출의 옵션.
 *
 * `force: true` 면 in-flight 요청이 있어도 abort 후 새 fetch 시작.
 * - 명시적 invalidate trigger (`useCoreUIDataRefetch`, retry, pageshow listener) 가 사용
 * - mount-time auto-fetch 는 force=false (dedup 유지)
 *
 * @see plan/cart-and-fetcher-override-design.md — §6-1
 */
export interface FetchEntryOptions {
  force?: boolean;
}

/**
 * Provider Context 값.
 *
 * 호스트에 직접 노출되지 않음 — `useCoreUIDataEntry` / `useCoreUIDataRefetch` hook 만이 사용자 인터페이스.
 *
 * - `env`: 호스트가 전달한 환경
 * - `entries`: 도메인별 entry 상태 (re-render trigger)
 * - `entriesRef`: entries 의 동기 ref — hook 안에서 stale closure 없이 즉시 읽기 위함
 * - `fetchEntry`: 해당 도메인의 fetch 시작 (이전 abort + new controller + setEntry loading → success/error)
 */
export interface CoreUIDataContextValue {
  env: Env;
  entries: Partial<Record<DomainKey, EntryState>>;
  entriesRef: { readonly current: Partial<Record<DomainKey, EntryState>> };
  fetchEntry: (key: DomainKey, options?: FetchEntryOptions) => void;
}

export interface CoreUIDataProviderProps {
  env: Env;
  /**
   * BFF 응답 분기 / 트래픽 분석용 platform 식별자. 모든 url-table 기반 fetch 의 `Nol-Platform` 헤더로 전송.
   *
   * **optional, default `'Web'`** — BFF 가 헤더 *무조건 받음* 정책이라 라이브러리가 default 책임.
   * 모바일 호스트는 정확한 트래픽 통계를 위해 `'MWeb'` 명시 권장. 미지정 시 dev 환경에서 `console.warn`
   * 으로 nudge — prod 는 silent.
   *
   * mount 시점 값만 사용 — 동적 변경 무시. 호스트가 의도적 platform 변경 원하면
   * `<Provider key={platform}>` 로 명시적 remount.
   *
   * host fetcher override (`fetchers.*`) 는 platform 안 받음 — host fetcher 안에서 호스트가
   * 자체 environment 의 platform 변수를 직접 헤더에 추가.
   *
   * **왜 required 아님**: 트래픽 분석용 데이터 — 정확성 요구가 낮음. required 의 type-level
   * nudge 비용 (모든 호스트가 detect 로직 작성) > 정확도 이득. silent inaccuracy 는 dev warn 으로 보조.
   */
  platform?: Platform;
  /**
   * fetch 실패 시 호출되는 트래킹 콜백.
   *
   * fallback override 와 무관하게 항상 호출 — 호스트가 site-wide 에러 로깅 가능.
   * 호스트 logging layer (clientLog, sentry 등) 와 연결 권장.
   *
   * @see plan/category-widget-design.md — Q14 b2
   */
  onError?: (domain: DomainKey, error: FetchError) => void;
  /**
   * 도메인별 fetcher override. 모두 optional.
   *
   * - 키 있음 → host 의 fetcher 호출 (auth wrapped 등 host 책임). url-table 무시
   * - 키 없음 → url-table 의 env-based fetcher 사용 (BC, category/bi 에 적용)
   * - `cart` 키 없음 + Header.CartAction mount → widget dev warn + showDot=false
   *
   * **type-level 가이드**: 각 도메인의 fetcher 는 `DomainResponseMap[K]` shape 으로 *반환 type 이 강제*됨.
   * host 가 BFF 의 raw 응답을 widget 기대 shape 으로 변환하는 어댑터를 누락하면 컴파일 에러.
   *
   * fetcher reference 안정성 불필요 — Provider 가 ref 로 latest 보관 (useCallback 불필요).
   *
   * @see plan/cart-and-fetcher-override-design.md — §5-1
   */
  fetchers?: { [K in DomainKey]?: HostFetcher<K> };
  children: React.ReactNode;
}
